import { type ApiListingRequest, TableWrapper } from '@bitstopco/bitstop-theme';
import { Card, CardContent, CardHeader, Divider, Grid, Stack } from '@mui/material';
import { skipToken } from '@reduxjs/toolkit/query/react';
import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import CassetteInventoryCard from '@/components/restocking/CassetteInventoryCard';
import CardTable from '@/components/ui/card/CardTable';

import { appendToFilters } from '@/helpers/api';
import { useGetMachineQuery } from '@/store/api/services/machines/machine';
import { useGetRestockRequestsQuery } from '@/store/api/services/restocking/restocking';

import { DEFAULT_PAGINATION } from '@/constants/api';
import {
  RESTOCKING_LOAD_ORDERS_COLUMNS,
  RESTOCKING_LOAD_ORDERS_FILTERS_CONFIG,
} from '@/constants/app/restocking';

import type { RestockRequestView } from '@/types/generated/athena';

const MachineInventory = () => {
  const { id = '0' } = useParams();
  const [payload, setPayload] = useState<ApiListingRequest>();
  const computedPayload = useMemo(() => {
    if (!payload) {
      return skipToken;
    }
    return appendToFilters(payload, [{ operator: 'equal', property: 'atm_id', value: id }]);
  }, [payload, id]);

  const {
    data: { data: machine = null } = {},
  } = useGetMachineQuery(Number.parseInt(id));

  const {
    data: {
      data: ordersData = [],
      meta: { pagination: metaPagination = DEFAULT_PAGINATION } = {},
    } = {},
    isFetching,
  } = useGetRestockRequestsQuery(computedPayload);

  const { columns, filters } = useMemo(
    () => ({
      columns: {
        ...RESTOCKING_LOAD_ORDERS_COLUMNS,
        items: RESTOCKING_LOAD_ORDERS_COLUMNS.items.filter(({ value }) => value !== 'atm_id'),
      },
      filters: RESTOCKING_LOAD_ORDERS_FILTERS_CONFIG.filter(
        ({ property }) => property !== 'atm_id',
      ),
    }),
    [machine],
  );

  return (
    <Stack mt={2}>
      <Card>
        <CardHeader title="Inventory" />
        <Divider />
        <CardContent>
          <Grid container spacing={2}>
            {machine?.note_dispenser_cassettes?.map((cassette) => (
              <Grid key={cassette.id} item xs={12} md={6} lg={3}>
                <CassetteInventoryCard atm_id={+id} cassette={cassette} />
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>
      <CardTable>
        <CardHeader title="Load Orders" />
        <Divider />
        <TableWrapper<RestockRequestView>
          data={ordersData}
          hideActionMenu
          isLoading={isFetching}
          sortByDefaultValue="created_at"
          columns={columns}
          dateRangeFilterKey="created_at"
          filterConfigs={filters}
          totalPages={metaPagination?.total_pages}
          totalItems={metaPagination?.total_items}
          tableProps={{
            placeholder: {
              empty: {
                title: 'No orders found',
                content:
                  "We can't find any order that matches your search, please try a different request",
              },
            },
          }}
          tableToolbarProps={{
            searchPlaceholder: 'Search code or ID',
          }}
          onChange={setPayload}
        />
      </CardTable>
    </Stack>
  );
};

export default MachineInventory;
